"use client"

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react"
import { User as SupabaseUser } from "@supabase/supabase-js"

// Base types that apps can extend
export interface BaseAuthUser {
  id: string
  email: string
  role: string
  metadata?: Record<string, any>
}

export interface BaseAuthContextType<TUser extends BaseAuthUser = BaseAuthUser> {
  // Supabase auth state
  supabaseUser: SupabaseUser | null
  isAuthenticated: boolean
  isLoading: boolean

  // App-specific user data
  user: TUser | null

  // Auth methods
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>

  // User profile methods
  loadUserProfile: (supabaseUser: SupabaseUser) => Promise<void>
  refreshProfile: () => Promise<void>
}

// Generic context - apps will create typed versions
const AuthContext = createContext<BaseAuthContextType | undefined>(undefined)

export interface AuthProviderProps {
  children: ReactNode
  supabaseClient: any // Apps provide their own Supabase client
  profileLoader: (supabaseUser: SupabaseUser) => Promise<BaseAuthUser | null>
  onAuthError?: (error: Error) => void
}

export function AuthProvider({
  children,
  supabaseClient,
  profileLoader,
  onAuthError
}: AuthProviderProps) {
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null)
  const [user, setUser] = useState<BaseAuthUser | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const loadUserProfile = async (supabaseUser: SupabaseUser) => {
    try {
      const userProfile = await profileLoader(supabaseUser)
      if (userProfile) {
        setUser(userProfile)
        setSupabaseUser(supabaseUser)
        setIsAuthenticated(true)
      }
    } catch (error) {
      console.error("Error loading user profile:", error)
      onAuthError?.(error as Error)
      throw error
    }
  }

  const refreshProfile = async () => {
    if (supabaseUser) {
      await loadUserProfile(supabaseUser)
    }
  }

  useEffect(() => {
    if (!supabaseClient) return

    // Get initial session
    const getSession = async () => {
      try {
        const { data: { session } } = await supabaseClient.auth.getSession()
        if (session?.user) {
          await loadUserProfile(session.user)
        }
      } catch (error) {
        console.error("Error getting session:", error)
        onAuthError?.(error as Error)
      } finally {
        setIsLoading(false)
      }
    }

    getSession()

    // Listen for auth changes
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(
      async (event: string, session: any) => {
        try {
          if (session?.user) {
            await loadUserProfile(session.user)
          } else {
            setSupabaseUser(null)
            setUser(null)
            setIsAuthenticated(false)
          }
        } catch (error) {
          console.error("Error handling auth state change:", error)
          onAuthError?.(error as Error)
        } finally {
          setIsLoading(false)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [supabaseClient])

  const login = async (email: string, password: string) => {
    if (!supabaseClient) throw new Error("Supabase client not available")

    const { error } = await supabaseClient.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
  }

  const logout = async () => {
    if (supabaseClient) {
      await supabaseClient.auth.signOut()
    }
    // Also clear local state
    setSupabaseUser(null)
    setUser(null)
    setIsAuthenticated(false)
  }

  return (
    <AuthContext.Provider value={{
      supabaseUser,
      user,
      isAuthenticated,
      isLoading,
      login,
      logout,
      loadUserProfile,
      refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth<TUser extends BaseAuthUser = BaseAuthUser>() {
  const context = useContext(AuthContext) as BaseAuthContextType<TUser> | undefined
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
